import type { DriverManifestStop } from "@/lib/logistics/driverTypes";
import { buildMapsUrl, buildTelUrl } from "@/lib/driver/driverMapLinks";
import { resolveFastFieldUrl } from "@/lib/driver/fastFieldLink";

type Props = {
  stop: DriverManifestStop;
  unitLabel: string;
  onStamp: (field: "arrived_at" | "departed_at") => void;
  stamping: boolean;
};

function formatStamp(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function addressLine(address: Record<string, unknown>): string {
  const parts = ["line1", "line2", "city", "state", "postal_code", "zip"]
    .map((k) => address[k])
    .filter((v): v is string => typeof v === "string" && v.trim().length > 0);
  return parts.join(", ") || "Address on file";
}

export function DriverStopCard({ stop, unitLabel, onStamp, stamping }: Props) {
  const mapsUrl = buildMapsUrl(stop.address);
  const telUrl = buildTelUrl(stop.contact_phone);
  const fastFieldUrl = resolveFastFieldUrl(stop.fastfield_url);

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Stop {stop.sequence_index + 1} · {stop.stop_kind.replace(/_/g, " ")}
          </p>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{unitLabel}</h3>
          {stop.contact_name ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">{stop.contact_name}</p>
          ) : null}
        </div>
      </div>

      <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{addressLine(stop.address)}</p>
      {stop.notes ? (
        <p className="mt-2 text-sm italic text-slate-600 dark:text-slate-400">{stop.notes}</p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        {mapsUrl ? (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg bg-slate-100 px-3 py-1.5 font-medium text-slate-800 dark:bg-slate-800 dark:text-slate-100"
          >
            Maps
          </a>
        ) : null}
        {telUrl ? (
          <a
            href={telUrl}
            className="rounded-lg bg-emerald-100 px-3 py-1.5 font-medium text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100"
          >
            Call
          </a>
        ) : null}
        <a
          href={fastFieldUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-lg bg-indigo-100 px-3 py-1.5 font-medium text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-100"
        >
          FastField
        </a>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-400">
        <div>
          <span className="font-semibold">Arrived</span>
          <p>{formatStamp(stop.arrived_at)}</p>
        </div>
        <div>
          <span className="font-semibold">Departed</span>
          <p>{formatStamp(stop.departed_at)}</p>
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={stamping}
          onClick={() => onStamp("arrived_at")}
          className="flex-1 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900"
        >
          Arrived
        </button>
        <button
          type="button"
          disabled={stamping}
          onClick={() => onStamp("departed_at")}
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-800 disabled:opacity-50 dark:border-slate-600 dark:text-slate-100"
        >
          Departed
        </button>
      </div>
    </article>
  );
}
